import { type ReactNode, useMemo } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WFPButton } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AdministrativeAreaTreeSelect,
  type AdministrativeAreaOption,
} from "@/components/AdministrativeAreaTreeSelect";
import { cn } from "@/lib/utils";
import { aggregatorLanguages } from "../../../../../../utils/i18n";

type OptionValue = string | number;

interface SelectOption {
  value: OptionValue;
  label: string;
  [key: string]: unknown;
}

export interface AggregatorDetailsProfileFormValues {
  name?: string;
  org_type?: SelectOption | null;
  language?: SelectOption | null;
  location?: AdministrativeAreaOption | null;
  physical_address?: string | null;
  is_wfp_vendor?: boolean | null;
  [key: string]: unknown;
}

interface AggregatorDetailsProfileProps {
  methods: UseFormReturn<AggregatorDetailsProfileFormValues>;
  showFieldErrors?: boolean;
  isSaving?: boolean;
  organizationTypes?: SelectOption[];
  administrativeAreas?: AdministrativeAreaOption[];
}

type ProfileFieldName = "name" | "org_type" | "language" | "location" | "physical_address";

interface FieldShellProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
  tooltip?: string;
  className?: string;
}

const toErrorMessage = (error: unknown) => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

const hasRequiredOptionValue = (value: unknown): value is { value: OptionValue } => {
  if (!value || typeof value !== "object" || !("value" in value)) {
    return false;
  }

  const optionValue = (value as { value?: unknown }).value;

  if (typeof optionValue === "number") {
    return true;
  }

  return typeof optionValue === "string" && optionValue.trim().length > 0;
};

const requiredString = (message: string) =>
  z.preprocess(
    value => (value === undefined || value === null ? "" : value),
    z.string().refine(value => value.trim().length > 0, { message })
  );

const requiredSelectOption = (message: string) =>
  z.custom<SelectOption>(hasRequiredOptionValue, { message });

const requiredAdministrativeArea = (message: string) =>
  z.custom<AdministrativeAreaOption>(hasRequiredOptionValue, { message });

export const createAggregatorDetailsProfileSchema = (requiredMessage: string) =>
  z.looseObject({
    name: requiredString(requiredMessage),
    org_type: requiredSelectOption(requiredMessage),
    language: requiredSelectOption(requiredMessage),
    location: requiredAdministrativeArea(requiredMessage),
    physical_address: z.string().nullable().optional(),
    is_wfp_vendor: z.boolean().nullable().optional().default(false),
  });

const toSelectValue = (value?: OptionValue | null) =>
  value === undefined || value === null ? undefined : String(value);

const findOptionByValue = (options: SelectOption[], value: string) =>
  options.find(option => toSelectValue(option.value) === value) ?? null;

const getLocationLevelPlaceholder = (
  locationValue: AdministrativeAreaOption | null | undefined,
  t: (key: string, options?: Record<string, string>) => string
) => {
  if (!locationValue) {
    return t("aggregatorDetails.form.locationPlaceholder");
  }

  const nextLevel = locationValue.children?.length
    ? [...new Set(locationValue.children.map(child => child.type).filter(Boolean))].join(" / ")
    : "Level";

  return t("aggregatorDetails.form.addLevelOptional", {
    level: nextLevel,
  });
};

const FieldShell = ({
  id,
  label,
  children,
  errorMessage,
  required = false,
  tooltip,
  className,
}: FieldShellProps) => (
  <div className={cn("space-y-2", className)}>
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="font-semibold text-foreground">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <WFPButton
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              aria-label={label}
            >
              <Info className="size-3.5" />
            </WFPButton>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-80">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
    {children}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

interface OptionSelectProps {
  id: string;
  value?: SelectOption | null;
  options: SelectOption[];
  placeholder: string;
  isDisabled?: boolean;
  errorMessage?: string;
  onBlur: () => void;
  onChange: (option: SelectOption | null) => void;
}

const OptionSelect = ({
  id,
  value,
  options,
  placeholder,
  isDisabled = false,
  errorMessage,
  onBlur,
  onChange,
}: OptionSelectProps) => (
  <Select
    value={toSelectValue(value?.value)}
    onValueChange={nextValue => onChange(findOptionByValue(options, nextValue))}
    disabled={isDisabled}
  >
    <SelectTrigger
      id={id}
      aria-invalid={!!errorMessage}
      aria-describedby={errorMessage ? `${id}-error` : undefined}
      onBlur={onBlur}
      className={cn(
        "h-10 w-full border-border",
        errorMessage && "border-destructive ring-destructive/20"
      )}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent position="popper" className="max-h-72">
      {options.map(option => {
        const optionValue = toSelectValue(option.value);
        if (!optionValue) {
          return null;
        }

        return (
          <SelectItem key={optionValue} value={optionValue}>
            {option.label}
          </SelectItem>
        );
      })}
    </SelectContent>
  </Select>
);

export const AggregatorDetailsProfile = ({
  methods,
  showFieldErrors = false,
  isSaving = false,
  organizationTypes = [],
  administrativeAreas = [],
}: AggregatorDetailsProfileProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = methods;

  const locationValue = watch("location");

  const languageOptions = useMemo<SelectOption[]>(
    () =>
      aggregatorLanguages.map((language: { engLabel?: string; label: string; value: string }) => ({
        label: language.engLabel ? `${language.engLabel} (${language.label})` : language.label,
        value: language.value,
      })),
    []
  );

  const getFieldError = (fieldName: ProfileFieldName) => {
    if (!showFieldErrors) {
      return "";
    }

    const error = errors[fieldName] as { message?: unknown } | undefined;
    return toErrorMessage(error?.message);
  };

  return (
    <Card
      aria-busy={isSaving}
      className="relative overflow-hidden border-neutral-alpha-200 bg-background shadow-none"
    >
      <CardContent className={cn("space-y-6", isSaving && "opacity-60")}>
        <div className="grid gap-5 md:grid-cols-2">
          <Controller
            control={control}
            name="name"
            render={({ field }) => {
              const errorMessage = getFieldError("name");

              return (
                <FieldShell
                  id="name-input"
                  label={t("aggregatorDetails.form.center")}
                  errorMessage={errorMessage}
                  required
                >
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    id="name-input"
                    placeholder={t("aggregatorDetails.form.insertName")}
                    aria-invalid={!!errorMessage}
                    aria-describedby={errorMessage ? "name-input-error" : undefined}
                    disabled={isSaving}
                    className="border-border"
                  />
                </FieldShell>
              );
            }}
          />
          <Controller
            control={control}
            name="org_type"
            render={({ field }) => {
              const errorMessage = getFieldError("org_type");

              return (
                <FieldShell
                  id="org_type-select"
                  label={t("aggregatorDetails.form.organizationType")}
                  errorMessage={errorMessage}
                  required
                >
                  <OptionSelect
                    id="org_type-select"
                    value={field.value}
                    options={organizationTypes}
                    placeholder={t("aggregatorDetails.form.organizationTypePlaceholder")}
                    isDisabled={isSaving}
                    errorMessage={errorMessage}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                </FieldShell>
              );
            }}
          />
        </div>

        <Controller
          control={control}
          name="language"
          render={({ field }) => {
            const errorMessage = getFieldError("language");

            return (
              <FieldShell
                id="language-select"
                label={t("aggregatorDetails.form.language")}
                errorMessage={errorMessage}
                required
              >
                <OptionSelect
                  id="language-select"
                  value={field.value}
                  options={languageOptions}
                  placeholder={t("aggregatorDetails.form.primaryLanguage")}
                  isDisabled={isSaving}
                  errorMessage={errorMessage}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              </FieldShell>
            );
          }}
        />

        <Controller
          control={control}
          name="location"
          render={({ field }) => {
            const errorMessage = getFieldError("location");

            return (
              <FieldShell
                id="location-treePicker"
                label={t("aggregatorDetails.form.location")}
                tooltip={t("aggregatorDetails.form.locationHintTooltip")}
                errorMessage={errorMessage}
                required
              >
                <AdministrativeAreaTreeSelect
                  id="location-treePicker"
                  value={field.value}
                  tree={administrativeAreas}
                  placeholder={getLocationLevelPlaceholder(locationValue, t)}
                  clearLabel={t("common.clearAll")}
                  hasError={!!errorMessage}
                  isDisabled={isSaving}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              </FieldShell>
            );
          }}
        />

        <Controller
          control={control}
          name="physical_address"
          render={({ field }) => {
            const errorMessage = getFieldError("physical_address");

            return (
              <FieldShell
                id="physical_address-input"
                label={t("aggregatorDetails.form.centerPhysicalAddress")}
                tooltip={t("aggregatorDetails.form.centerPhysicalAddressInfoTip")}
                errorMessage={errorMessage}
              >
                <Textarea
                  {...field}
                  value={field.value ?? ""}
                  id="physical_address-input"
                  placeholder={t("aggregatorDetails.form.insertPhysicalAddress")}
                  aria-invalid={!!errorMessage}
                  aria-describedby={errorMessage ? "physical_address-input-error" : undefined}
                  disabled={isSaving}
                  className="min-h-20 resize-y border-border"
                />
              </FieldShell>
            );
          }}
        />

        <Controller
          control={control}
          name="is_wfp_vendor"
          render={({ field }) => (
            <div className="rounded-lg border border-border px-4 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Label htmlFor="is_wfp_vendor-switch" className="font-semibold text-foreground">
                  {t("aggregatorDetails.form.isWfpVendor")}
                </Label>
                <Switch
                  id="is_wfp_vendor-switch"
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isSaving}
                />
              </div>
            </div>
          )}
        />
      </CardContent>
      {isSaving && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <div className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3 text-sm font-medium text-muted-foreground shadow-sm">
            <Spinner className="size-5 text-primary" />
            <span>{t("loading.defaultMessage")}</span>
          </div>
        </div>
      )}
    </Card>
  );
};
